import React, { useState, useEffect, useMemo } from 'react';
import { bookingApi } from '../../../utils/bookingApi';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDate } from '../../../utils/dates';
import {
  PageHeader, Sheet, Picker, Skeleton, ErrorState, InlineError, useConfirm, useToast,
} from '../../../components/ui';
import '../booking-shared.css';
import './adminTables.css';

const ADMIN = { to: '/admin-hub', label: 'Admin' };
const EMPTY_FORM = { userId: '', amount: '', startDate: '', endDate: '' };
const pounds = (pence) => `£${(pence / 100).toFixed(2)}`;

// A credit with no end runs until it's cancelled
const endText = (iso) => (iso ? formatDate(iso) : 'Indefinite');

// Adding a recurring credit: who, how much a month, and optionally when
function RecurringCreditSheet({ members, onClose, onAdded }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const items = useMemo(() => members.map(m => ({ id: m.id, label: `${m.firstName} ${m.lastName}` })), [members]);
  const dirty = Object.keys(EMPTY_FORM).some(k => form[k] !== EMPTY_FORM[k]);

  const submit = async () => {
    setError(null);
    const amountPence = Math.round(parseFloat(form.amount) * 100);
    const problems = {};
    if (!form.userId) problems.userId = 'Choose a member';
    if (!Number.isFinite(amountPence) || amountPence < 1) problems.amount = 'Enter an amount above £0';
    setFieldErrors(problems);
    if (Object.keys(problems).length) { setError('Check the highlighted fields.'); return; }
    setSubmitting(true);
    try {
      const payload = { userId: form.userId, amountPence };
      if (form.startDate) payload.startDate = form.startDate;
      if (form.endDate) payload.endDate = form.endDate;
      await bookingApi.createRecurringCredit(payload);
      onAdded();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't add the recurring credit.");
      setSubmitting(false);
    }
  };

  return (
    <Sheet
      title="Add recurring credit"
      onClose={onClose}
      dirty={dirty}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Add'}
          </button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="bk-label">
        <label htmlFor="rc-member">Member</label>
        <Picker
          id="rc-member"
          label="Member"
          items={items}
          value={form.userId}
          onChange={id => setForm(f => ({ ...f, userId: id }))}
          placeholder="Search by name…"
          required
        />
        {fieldErrors.userId && <span className="bk-field-error">{fieldErrors.userId}</span>}
      </div>
      <label className="bk-label">Monthly amount (£)
        <input
          type="number" min="0.01" step="0.01" inputMode="decimal" className="bk-input"
          value={form.amount} required aria-invalid={fieldErrors.amount ? 'true' : undefined}
          onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
        />
        {fieldErrors.amount && <span className="bk-field-error">{fieldErrors.amount}</span>}
      </label>
      <label className="bk-label">Start date<span className="bk-optional"> (optional)</span>
        <input type="date" className="bk-input" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
      </label>
      <label className="bk-label">End date<span className="bk-optional"> (optional)</span>
        <input type="date" className="bk-input" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
      </label>
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </Sheet>
  );
}

export default function AdminCredits() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();
  // Adding and cancelling recurring credits needs Manage access to payments
  const { can } = useAuth();
  const managesPayments = can('payments', 'MANAGE');

  // ── Recurring credits ─────────────────────────────────────────────────────
  const [members, setMembers] = useState([]);
  const [rules, setRules] = useState([]);
  const [rulesError, setRulesError] = useState(null);
  const [adding, setAdding] = useState(false);

  const loadCredits = () => {
    setLoading(true);
    setLoadError(false);
    return bookingApi.getAllCredits()
      .then(r => setUsers(r.data.filter(u => u.credits && u.credits.length > 0)))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  const loadRecurring = () =>
    bookingApi.getRecurringCredits()
      .then(r => { setRules(r.data); setRulesError(null); })
      .catch(() => setRulesError("Couldn't load recurring credits."));

  useEffect(() => {
    loadCredits();
    loadRecurring();
    // Names only (Finance can't see the member list)
    if (managesPayments) {
      bookingApi.getPayerChoices()
        .then(r => setMembers(r.data))
        .catch(() => setRulesError("Couldn't load the members to choose from."));
    }
  }, [managesPayments]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelRule = async (rule) => {
    const ok = await confirm({
      title: 'Cancel this recurring credit?',
      message: `${rule.userName} won't be given any more of it. Credit already given stays.`,
      confirmLabel: 'Cancel credit',
      cancelLabel: 'Keep it',
      tone: 'primary',
    });
    if (!ok) return;
    try {
      await bookingApi.deleteRecurringCredit(rule.id);
      toast('Recurring credit cancelled');
      loadRecurring();
    } catch {
      setRulesError("Couldn't cancel the recurring credit. Try again.");
    }
  };

  const header = (
    <PageHeader
      back={ADMIN}
      title="Credits"
      action={managesPayments ? { label: 'Add recurring credit', short: 'Add', onClick: () => setAdding(true) } : undefined}
    />
  );

  if (loading) return <div className="bk-page bk-page--wide">{header}<Skeleton rows={4} label="Loading credits…" /></div>;
  if (loadError) return <div className="bk-page bk-page--wide">{header}<ErrorState title="Couldn't load credits" onRetry={loadCredits} /></div>;

  return (
    <div className="bk-page bk-page--wide">
      {header}

      {users.length === 0 ? (
        <p className="bk-muted">No active credits.</p>
      ) : (
        <table className="bk-table ad-table">
          <thead>
            <tr>
              <th>Member</th>
              <th className="num">Amount</th>
              <th>Expires</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {users.flatMap(u =>
              u.credits.map(c => (
                <tr key={c.id}>
                  <td className="ad-card-title">{u.firstName} {u.lastName}</td>
                  <td className="num ad-card-status"><strong>{pounds(c.amount)}</strong></td>
                  <td className="ad-nowrap" data-label="Expires">{endText(c.expiresAt)}</td>
                  <td data-label="Note">{c.note || <span className="bk-muted">None</span>}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {/* ── Recurring credits ────────────────────────────────────────────── */}
      <h2 className="bk-section-title">Recurring credits</h2>
      {rulesError && <InlineError onRetry={loadRecurring}>{rulesError}</InlineError>}
      {rules.length === 0 && !rulesError && <p className="bk-muted">No recurring credits.</p>}

      {rules.length > 0 && (
        <table className="bk-table ad-table">
          <thead>
            <tr>
              <th>Member</th>
              <th className="num">Monthly amount</th>
              <th>Start date</th>
              <th>End date</th>
              <th>Last issued</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.id}>
                <td className="ad-card-title">{r.userName}</td>
                <td className="num ad-card-status"><strong>{pounds(r.amountPence)}</strong> <span className="bk-muted">a month</span></td>
                <td className="ad-nowrap" data-label="Starts">{r.startDate ? formatDate(r.startDate) : <span className="bk-muted">Not set</span>}</td>
                <td className="ad-nowrap" data-label="Ends">{endText(r.endDate)}</td>
                <td className="ad-nowrap" data-label="Last issued">{r.lastIssuedAt ? formatDate(r.lastIssuedAt) : <span className="bk-muted">Not yet</span>}</td>
                <td className="ad-card-action">
                  {managesPayments && (
                    <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={() => handleCancelRule(r)}>
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <RecurringCreditSheet
          members={members}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); toast('Recurring credit added'); loadRecurring(); }}
        />
      )}
    </div>
  );
}
